/**
 * Auth Provider Component
 *
 * Initializes authentication state on app mount.
 * Attempts to refresh the access token using the refresh token cookie.
 * Skips auth initialization on public routes to avoid unnecessary 401 errors.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useInitAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';

const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password'];

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some(route => pathname.startsWith(route));
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const location = useLocation();
  const initAuth = useInitAuth();
  const setLoading = useAuthStore((state) => state.setLoading);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isPublicRoute(location.pathname)) {
      // Skip auth initialization on public routes
      setLoading(false);
      return;
    }

    // Only initialize once, even with StrictMode double-mounting
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      initAuth.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return <>{children}</>;
}

export default AuthProvider;
